// Expediente MedOS de una persona a través de las jornadas de la Ruta de la Salud:
// visitas, tendencias de sus mediciones y canalizaciones. Lo arma el servidor
// (GET /gov-health/expedientes/{id}) y cada apertura queda en la bitácora de accesos.

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot, FileHeart, Lock, MapPin, Send, X } from 'lucide-react';

import { EmptyState, ErrorNote, Loading, Sparkline } from '@/modules/orion-shell/ui';

import { govHealthApi } from '../api';
import type { Atencion, Bandera, Mediciones } from '../types';
import { OrigenBadge, fechaCorta } from './parts';

export const BANDERA_CLS: Record<string, string> = {
  alta: 'bg-red-500/15 text-red-700 dark:text-red-300',
  media: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  baja: 'bg-muted text-muted-foreground',
};

export function BanderaChips({ banderas }: { banderas: Bandera[] }) {
  if (!banderas.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {banderas.map((b) => (
        <span key={b.clave} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BANDERA_CLS[b.nivel] ?? BANDERA_CLS.baja}`}>
          {b.etiqueta}{b.n != null ? ` · ${b.n}` : ''}
        </span>
      ))}
    </span>
  );
}

export function medicionesTexto(m: Mediciones): string {
  const p: string[] = [];
  if (m.ta_sistolica != null) p.push(`TA ${m.ta_sistolica}/${m.ta_diastolica}`);
  if (m.glucosa_capilar != null) p.push(`Glucosa ${m.glucosa_capilar}`);
  if (m.peso != null) p.push(`${m.peso} kg`);
  if (m.imc != null) p.push(`IMC ${m.imc}`);
  return p.join(' · ');
}

export const ESTADO_CANAL_CLS: Record<string, string> = {
  atendida: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', no_acudio: 'bg-red-500/15 text-red-700 dark:text-red-300',
  agendada: 'bg-primary/10 text-primary', enviada: 'bg-muted text-foreground', cancelada: 'bg-muted text-muted-foreground',
};

const SERIES: { code: string; label: string; unit: string; pair?: string }[] = [
  { code: 'glucosa_capilar', label: 'Glucosa capilar', unit: 'mg/dL' },
  { code: 'ta_sistolica', label: 'Presión arterial', unit: 'mmHg', pair: 'ta_diastolica' },
  { code: 'peso', label: 'Peso', unit: 'kg' },
];

export function ExpedienteDrawer({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const h = useQuery({ queryKey: ['gov-health', 'historial', patientId], queryFn: () => govHealthApi.historial(patientId), retry: false });
  const d = h.data;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" role="dialog" aria-modal="true" aria-label="Expediente MedOS"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-border bg-background shadow-2xl">
        <header className="flex items-start gap-3 border-b border-border px-5 py-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><FileHeart size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Expediente MedOS</p>
            <h2 className="truncate text-lg font-semibold">{d?.display_name ?? 'Cargando…'}</h2>
            {d && (
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{d.edad != null ? `${d.edad} años` : 'Edad sin registrar'}{d.sexo ? ` · ${d.sexo === 'F' ? 'Mujer' : d.sexo === 'M' ? 'Hombre' : d.sexo}` : ''}</span>
                <span className="font-mono">{d.external_id}</span>
                {d.is_demo && <OrigenBadge origen="demostracion" />}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" autoFocus className="grid h-9 w-9 place-items-center rounded-xl hover:bg-muted"><X size={16} /></button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {h.isLoading ? <Loading /> : h.isError ? (
            (h.error as any)?.status === 403
              ? <EmptyState icon={<Lock size={18} />} title="Necesitas permiso clínico">El expediente solo lo abre personal de salud con acceso clínico.</EmptyState>
              : <ErrorNote>{(h.error as Error).message}</ErrorNote>
          ) : d && (
            <div className="flex flex-col gap-5">
              {d.resumen && <p className="rounded-2xl bg-muted/60 px-4 py-3 text-sm leading-relaxed">{d.resumen}</p>}
              {d.banderas_actuales.length > 0 && (
                <div><p className="mb-1.5 text-xs font-semibold text-muted-foreground">Banderas de la última visita</p><BanderaChips banderas={d.banderas_actuales} /></div>
              )}

              <section aria-label="Tendencias">
                <h3 className="mb-2 text-sm font-semibold">Cómo ha cambiado entre jornadas</h3>
                <div className="grid gap-2 sm:grid-cols-3">
                  {SERIES.map((s) => {
                    const serie = d.series[s.code] ?? [];
                    const pair = s.pair ? d.series[s.pair] ?? [] : [];
                    if (!serie.length) return null;
                    const v = (i: number) => (s.pair ? `${serie[i].valor}/${pair[i]?.valor ?? '—'}` : `${serie[i].valor}`);
                    return (
                      <div key={s.code} className="rounded-xl border border-border p-3">
                        <p className="text-[11px] font-medium text-muted-foreground">{s.label}</p>
                        <p className="mt-0.5 text-base font-semibold tabular-nums">
                          {serie.length > 1 ? <>{v(0)} <span className="text-muted-foreground">→</span> {v(serie.length - 1)}</> : v(0)}
                          <span className="ml-1 text-[11px] font-normal text-muted-foreground">{s.unit}</span>
                        </p>
                        {serie.length > 1 && <Sparkline data={serie.map((p) => p.valor)} />}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section aria-label="Visitas a la Ruta de la Salud">
                <h3 className="mb-2 text-sm font-semibold">Visitas a la Ruta de la Salud ({d.visitas.length})</h3>
                {d.visitas.length === 0 ? <p className="text-sm text-muted-foreground">Sin visitas registradas en jornadas.</p> : (
                  <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
                    {[...d.visitas].reverse().map((v) => <VisitaItem key={v.id} v={v} />)}
                  </ol>
                )}
              </section>

              {d.canalizaciones.length > 0 && (
                <section aria-label="Canalizaciones">
                  <h3 className="mb-2 text-sm font-semibold">Canalizaciones</h3>
                  <ul className="flex flex-col gap-2">
                    {d.canalizaciones.map((c) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm">
                        <Send size={13} className="text-muted-foreground" />
                        <span className="min-w-0 flex-1">{c.motivo_label} → <span className="font-medium">{c.destino_label}</span></span>
                        <span className="text-xs text-muted-foreground">{fechaCorta(c.fecha)}</span>
                        {c.prioridad === 'urgente' && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">Urgente</span>}
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ESTADO_CANAL_CLS[c.estado] ?? ''}`}>{c.estado_label}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(d.alergias.length > 0 || d.condiciones.length > 0) && (
                <section aria-label="Antecedentes" className="text-sm">
                  <h3 className="mb-1 font-semibold">Antecedentes en MedOS</h3>
                  {d.alergias.map((a) => <p key={a.sustancia}>Alergia: {a.sustancia} ({a.severidad})</p>)}
                  {d.condiciones.map((c) => <p key={c.cie10 + c.nombre}>{c.nombre} {c.cie10 && <span className="text-muted-foreground">· {c.cie10}</span>}</p>)}
                </section>
              )}
            </div>
          )}
        </div>
        <footer className="flex items-center gap-2 border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
          <Lock size={12} /> {d?.aviso ?? 'Expediente MedOS: datos cifrados; cada consulta queda en la bitácora de accesos.'}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function VisitaItem({ v }: { v: Atencion }) {
  const med = medicionesTexto(v.mediciones);
  return (
    <li className="relative">
      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary" aria-hidden />
      <p className="text-sm font-semibold">{fechaCorta(v.fecha)} <span className="font-normal text-muted-foreground">· {v.hora}</span></p>
      <p className="text-xs text-muted-foreground"><MapPin size={11} className="mr-0.5 inline" />{v.sede ?? v.colonia}</p>
      <p className="mt-1 text-xs">{v.servicios.map((s) => s.etiqueta).join(' · ')}</p>
      {med && <p className="mt-0.5 text-xs font-medium tabular-nums">{med}</p>}
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <BanderaChips banderas={v.banderas} />
        {v.canalizacion && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ESTADO_CANAL_CLS[v.canalizacion.estado] ?? ''}`}>
            Canalizada a {v.canalizacion.destino_label} · {v.canalizacion.estado_label}
          </span>
        )}
        {v.via_agente && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"><Bot size={11} />vía agente</span>}
      </div>
    </li>
  );
}
